import React from 'react';
import {Box, Typography, CircularProgress, Alert} from '@mui/material';
import {useQuery, useMutation} from '@apollo/client';
import {
  GET_MY_CONNECTIONS,
  GET_ORG_CONNECTIONS,
  GET_INTEGRATION_CATALOG,
} from '../../graphql/queries';
import {
  CREATE_CONNECT_SESSION,
  REVOKE_CONNECTION,
  PROMOTE_CONNECTION_TO_USER,
} from '../../graphql/mutations';
import {ConnectionCard} from './ConnectionCard';
import {
  ConnectionTier,
  type IntegrationConnection,
} from '../../types/generated/graphql';

type ConnectionStatusFilter = 'ACTIVE_ONLY' | 'TERMINAL_ONLY' | 'ALL';

interface Props {
  tierFilter: ConnectionTier;
  orgId?: string;
  emptyMessage?: string;
  /**
   * S8: render every card in status-only mode (no Reconnect, no
   * Revoke). The Org Integrations page passes `readonly` for
   * non-admin viewers; server-side `requireOrgAdmin` is the gate.
   */
  readonly?: boolean;
  /**
   * S7: which lifecycle bucket to show. Defaults to ACTIVE_ONLY so
   * the existing call sites (Org/Personal Integrations) keep their
   * "what's live" framing. The My Connections "Inactive" panel
   * passes TERMINAL_ONLY to render the audit trail of revoked /
   * disconnected connections. The resolver already filters
   * DISCONNECTED on the server, so TERMINAL_ONLY effectively means
   * "REVOKED + EXPIRED" today.
   */
  statusFilter?: ConnectionStatusFilter;
  /**
   * S7: pass through to each `ConnectionCard` so My Connections can
   * show last-used / expiry / shared-with-N-orgs metadata without
   * bloating the more compact Org card.
   */
  showMetadata?: boolean;
  /**
   * S9: when true, expose the "Save permanently" CTA on RUN-tier
   * cards whose integration manifest allows USER. The list looks
   * the eligibility up itself via `GET_INTEGRATION_CATALOG` so
   * call sites don't have to thread it through. Off by default —
   * the Org pane never wants to suggest USER promotion.
   */
  enableRunPromotion?: boolean;
}

const ACTIVE_STATUSES: ReadonlySet<string> = new Set(['ACTIVE', 'ERROR', 'EXPIRED']);
const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['REVOKED', 'DISCONNECTED']);

export const ConnectionList: React.FC<Props> = ({
  tierFilter,
  orgId,
  emptyMessage = 'No integrations connected yet.',
  readonly = false,
  statusFilter = 'ACTIVE_ONLY',
  showMetadata = false,
  enableRunPromotion = false,
}) => {
  const isOrg = tierFilter === ConnectionTier.Org;
  const isRunTier = tierFilter === ConnectionTier.Run;

  const myQuery = useQuery(GET_MY_CONNECTIONS, {skip: isOrg});
  const orgQuery = useQuery(GET_ORG_CONNECTIONS, {
    skip: !isOrg || !orgId,
    variables: {orgId: orgId ?? ''},
  });

  // Only fetch the catalog when we'll actually use it (the
  // promotion CTA only fires for RUN-tier rows on personal pages
  // and we need the catalog to know if USER promotion is allowed).
  const catalogQuery = useQuery(GET_INTEGRATION_CATALOG, {
    skip: !enableRunPromotion || !isRunTier,
  });
  const userPromotableIntegrationIds = React.useMemo(() => {
    const out = new Set<string>();
    const entries = catalogQuery.data?.integrationCatalog ?? [];
    for (const entry of entries) {
      if (entry.allowedTiers?.includes(ConnectionTier.User)) {
        out.add(entry.id);
      }
    }
    return out;
  }, [catalogQuery.data]);

  const active = isOrg ? orgQuery : myQuery;

  const [createSession] = useMutation(CREATE_CONNECT_SESSION);
  const [revoke, {loading: revoking}] = useMutation(REVOKE_CONNECTION, {
    onCompleted: () => active.refetch(),
  });
  const [promote, {loading: promoting}] = useMutation(
    PROMOTE_CONNECTION_TO_USER,
    {
      // Promotion mints a USER row + revokes the RUN row, so both
      // the RUN list and the USER list need to repaint. We can't
      // know which queries the parent page mounted, so refetch the
      // active one and let the parent's outer queries refresh on
      // their own polling. The My Connections page additionally
      // refetches GET_MY_CONNECTIONS via its update hook, but for
      // standalone callers this is the safe default.
      onCompleted: () => active.refetch(),
    },
  );

  const handlePromote = async (connectionId: string) => {
    try {
      await promote({variables: {id: connectionId}});
    } catch (err) {
      console.error('Failed to promote connection to USER tier:', err);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    await revoke({variables: {id: connectionId}});
  };

  const handleReauth = async (integrationId: string) => {
    try {
      const result = await createSession({
        variables: {
          integrationId,
          tier: tierFilter,
          orgId: isOrg ? orgId ?? null : null,
          runId: null,
        },
      });
      const url = result.data?.createConnectSession?.connectUrl;
      if (url) {
        window.open(url, '_blank', 'width=600,height=700');
      }
    } catch (err) {
      console.error('Failed to create connect session:', err);
    }
  };

  if (active.loading) return <CircularProgress size={24} />;
  if (active.error) return <Alert severity="error">{active.error.message}</Alert>;

  const connections: IntegrationConnection[] = isOrg
    ? orgQuery.data?.orgConnections ?? []
    : myQuery.data?.myConnections ?? [];

  const filtered = connections
    .filter((c) => c.tier === tierFilter)
    .filter((c) => {
      if (statusFilter === 'ALL') return true;
      if (statusFilter === 'TERMINAL_ONLY') return TERMINAL_STATUSES.has(c.status);
      return ACTIVE_STATUSES.has(c.status);
    });

  if (filtered.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box>
      {filtered.map((conn) => (
        <ConnectionCard
          key={conn.id}
          connection={conn}
          onDisconnect={handleDisconnect}
          onReauth={handleReauth}
          disconnecting={revoking}
          readonly={readonly}
          showMetadata={showMetadata}
          canPromoteToUser={
            enableRunPromotion &&
            isRunTier &&
            userPromotableIntegrationIds.has(conn.integrationId)
          }
          onPromoteToUser={handlePromote}
          promoting={promoting}
        />
      ))}
    </Box>
  );
};
